import { ReportAssessmentData } from "../models/interfaces/reports/report-assessment-data";
import { ReportComplianceData } from "../models/interfaces/reports/report-compliance-data";
import { ReportDetailData } from "../models/interfaces/reports/report-detail-data";
import { ReportSummaryData } from "../models/interfaces/reports/report-summary-data";
import { UserTag } from "../models/interfaces/user-tag";
import { MskScore } from "../models/interfaces/msk-score";
import { DateTime } from "luxon";
import { AssessmentUtils } from "../utilities/assessments/assessment-utils";
import { ReportType } from "../models/enumerations/report-type";
import ReportService from "../utilities/services/report-service";
import { Report } from "../models/interfaces/report";
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from "notistack";
import i18n from "../i18n";
import MskScoreUtil from "./msk-score-util";
import UserService from "../utilities/services/user-service";
import { createLogger } from "./logger";
import { User } from "../models/interfaces/user";
import { GroupIds } from "../models/enumerations/group-ids";
import { ReportMovementData } from "../models/interfaces/reports/report-movement-data";
import { ReportWellnessSummaryData } from "../models/interfaces/reports/report-wellness-summary-data";
import { WellnessProgramStatus, WellnessProgramStatusTranslation } from "../models/interfaces/wellness-program-status";
import { DetailUserRow } from "../models/interfaces/detail-user-row";
import FunctionService from "./services/function-service";
import ReportDataService from "./services/report-data-service";
import { DetailReportFilterState } from "../models/interfaces/detail-report-filter-state";
import OrganizationService from "./services/organization-service";
import UserLatestMskScoreService from "./services/user-latest-msk-score-service";
import mskScoreMonthlyMskScoreService from "./services/user-monthly-msk-score-service";
import FirestoreCondition from "../models/interfaces/firestore-condition";
import { Timestamp } from "firebase/firestore";
import { FullMskHealthRiskOptions, MskHealthRiskOptions, MskHealthRiskValues } from "../models/msk-health-risk";
import { MovementFocusOptions, MovementFocusOptionsWithBreathing } from "../models/movement-focus-group";
import { MovementUserRow } from "../models/interfaces/movement-user-row";
import { ReportUserMovementItem } from "../models/interfaces/reports/report-user-movement-item";
import { UserStatus } from "../models/enumerations/user-status";
import UserActivity from "../models/interfaces/user-activity";
import { ActivityType } from "../models/enumerations/activity-type";
import UserActivityService from "./services/user-activity-service";
import { ReportDynamicComplianceData } from "../models/interfaces/reports/report-dynamic-compliance-data";
import { FilterState } from "../models/interfaces/filter-state";
import { WellnessProgram } from "../models/interfaces/wellness-program";
import WellnessProgramService from "./services/wellness-program-service";
import FirestoreOrder from "../models/interfaces/firestore-order";
import { WellnessProgramActivity } from "../models/enumerations/wellness-program-activity";
import { ReportDynamicWellnessSummaryData } from "../models/interfaces/reports/report-dynamic-wellness-summary-data";
import { ReportFilterState } from "../models/interfaces/report-filter-state";
import { ReportDynamicSummaryData } from "../models/interfaces/reports/report-dynamic-summary-data";
import { Utils } from "./utils";
import { ReportDynamicDetailData } from "../models/interfaces/reports/report-dynamic-detail-data";
import { ReportDynamicScoreData } from "../models/interfaces/reports/report-dynamic-score-data";
import TagService from "./services/tag-service";
import GroupService from "./services/group-service";
import { Group } from "../models/interfaces/group";
import { Tag } from "../models/interfaces/tag";
import { PainFilterOption } from "../models/enumerations/pain-filter-option";
import { DetailFilterState } from "../models/interfaces/detail-filter-state";
import { ReportDynamicMovementData } from "../models/interfaces/reports/report-dynamic-movement-data";
import { FocusGroupOptions } from "../models/focus-group";

const log = createLogger("ReportDataUtil");


interface DetailRow {
    name: string;
    email: string;
    age: number | string;
    group: string;
    tags: string;
    mskScore: number | string;
    mskRisk: string;
    pain: string;
    focus1: string;
    focus2: string;
}

interface ScoreRow {
    name: string;
    email: string;
    age: number | string;
    group: string;
    tags: string;
    movementScore: number | string;
    breathingScore: number | string;
    injuryScore: number | string;
    behavioralScore: number | string;
    activityScore: number | string;
    bodyCompScore: number | string;
    sleepScore: number | string;
    nutritionScore: number | string;
    mskScore: number | string;
}

interface SummaryRow {
    organization: string;
    month: string;
    users: number | string;
    currentMsk: number | string;
    month8: number | string;
    month7: number | string;
    month6: number | string;
    month5: number | string;
    month4: number | string;
    month3: number | string;
    month2: number | string;
    month1: number | string;
    scores0to9: number | string;
    scores10to19: number | string;
    scores20to29: number | string;
    scores30to39: number | string;
    scores40to49: number | string;
    scores50to59: number | string;
    scores60to69: number | string;
    scores70to79: number | string;
    scores80to89: number | string;
    scores90plus: number | string;
    highRisk: number | string;
    moderateRisk: number | string;
    slightRisk: number | string;
    lowRisk: number | string;
    movementHealth: number | string;
    breathingQuality: number | string;
    injuryHistory: number | string;
    behavioralHealth: number | string;
    physicalActivity: number | string;
    bodyComposition: number | string;
    sleepQuality: number | string;
    nutritionalAwareness: number | string;
    toeTouch: number | string;
    balanceAndReach: number | string;
    shoulderMobility: number | string;
    shoulderClearing: number | string;
    rotation: number | string;
    spineClearing: number | string;
    squat: number | string;
    pain: number | string;
}

interface ComplianceRow {
    group: string;
    tags: string;
    users: number | string;
    pendingUsers: number | string;
    loggedIntoApp: number | string;
    takenAssessment: number | string;
    completed50PercentOfTasks: number | string;
    pendingAssessment: string;
    pendingInitialAssessment: string;
}

interface MovementRow {
    name: string;
    email: string;
    age: number | string;
    group: string;
    tags: string;
    toeTouch: number | string;
    shoulderMobility: number | string;
    rotation: number | string;
    balance: number | string;
    squat: number | string;
    shoulderClearing: string;
    spineClearing: string;
    total: number | string;
    focus: string;
}

interface WellnessSummaryRow {
    group: string;
    tags: string;
    users: number | string;
    averageEngagement: number | string;
    sevenDayEngagement: number | string;
    thirtyDayEngagement: number | string;
    averageDaysMissed: number | string;
    averageStreak: number | string;
    activePrograms: number | string;
    pendingRetest: number | string;
    enrolledUsers: number | string;
    name: string;
    programStatus: WellnessProgramStatus | string;
    daysLeft: number | string;
    daysMissed: number | string;
    currentStreak: number | string;
    userSevenDayEngagement: number | string;
    userThirtyDayEngagement: number | string;
    progressPercentage: number | string;
}
export default class ReportDataUtil {




    // -----------------------------------------------------------------------------------------
    // #region Public Methods
    // -----------------------------------------------------------------------------------------

    public static generateAssessment(data: ReportAssessmentData) {
        // TODO - no csv example provided
    }

    public static generateCompliance(data: ReportComplianceData) {
        const result: ComplianceRow[] = [];
        result.push({
            group: '',
            tags: '',
            users: data.totalUsers,
            pendingUsers: data.pendingUsers,
            loggedIntoApp: data.usersLoggedIn,
            takenAssessment: data.usersAssessment,
            completed50PercentOfTasks: data.usersCompletedTasks,
            pendingAssessment: '',
            pendingInitialAssessment: '',
        });

        const emptyRow = {
            group: '',
            tags: '',
            users: '',
            pendingUsers: '',
            loggedIntoApp: '',
            takenAssessment: '',
            completed50PercentOfTasks: '',
            pendingAssessment: '',
            pendingInitialAssessment: '',
        };

        data.pendingLoginUsers.forEach((pl, i) => {
            if (result.length >= i + 1) {
                result[i].pendingInitialAssessment = pl;
            } else {
                const newRow = { ...emptyRow };
                newRow.pendingInitialAssessment = pl;
                result.push(newRow);
            }
        });

        data.pendingAssessmentUsers.forEach((pa, i) => {
            if (result.length >= i + 1) {
                result[i].pendingAssessment = pa;
            } else {
                const newRow = { ...emptyRow };
                newRow.pendingAssessment = pa;
                result.push(newRow);
            }
        })

        data.groupNames?.forEach((gn, i) => {
            if (result.length >= i + 1) {
                result[i].group = gn;
            } else {
                const newRow = { ...emptyRow };
                newRow.group = gn;
                result.push(newRow);
            }
        });

        data.tagNames?.forEach((tn, i) => {
            if (result.length >= i + 1) {
                result[i].tags = tn;
            } else {
                const newRow = { ...emptyRow };
                newRow.tags = tn;
                result.push(newRow);
            }
        });

        return this.generateComplianceCsv(result);
    }

    private static generateComplianceCsv(data: ComplianceRow[]) {
        let csvStr = '';
        const headers = i18n.t('utils.reportData.complianceHeaders') + '\n'
        csvStr += headers;
        data.forEach((r) => {
            let row = '';
            row += r.group + ',';
            row += r.tags + ',';
            row += r.users + ',';
            row += r.pendingUsers + ',';
            row += r.loggedIntoApp + ',';
            row += r.takenAssessment + ',';
            row += r.completed50PercentOfTasks + ',';
            row += ' ,';
            row += r.pendingAssessment + ',';
            row += r.pendingInitialAssessment + '\n';
            csvStr += row;
        });
        return csvStr;
    }

    public static generateDetail(data: DetailUserRow[]) {
        const result: DetailRow[] = [];

        const getMskRisk = (r: string | undefined) => {
            switch (r) {
                case 'Health':
                    return i18n.t('general.mskRisk.high');
                case 'Wellness':
                    return i18n.t('general.mskRisk.moderate');
                case 'Fitness':
                    return i18n.t('general.mskRisk.slight');
                case 'Performance':
                    return i18n.t('general.mskRisk.low');
                default:
                    return '';
            }
        }

        data.forEach((u) => {
            const userTags = this.stringTags(u.tags);
            const row: DetailRow = {
                name: u.name,
                email: u.email,
                age: u.dob ? this.getAgeFromDob(u.dob) : '',
                group: u.groupName ?? '',
                tags: userTags,
                mskScore: u.mskScore ?? '',
                mskRisk: getMskRisk(u.healthCategory),
                pain: u.inPain === true ? 'yes' : 'no',
                focus1: '',
                focus2: '',
            };

            if (u.focusAreas && u.focusAreas.length > 0) {
                row.focus1 = u.focusAreas[0];
                if (u.focusAreas.length >= 2) {
                    row.focus2 = u.focusAreas[1];
                }
            }

            result.push(row);
        })

        return this.generateDetailCsv(result);
    }

    private static generateDetailCsv(data: DetailRow[]) {
        const escapeCsv = (value: any) => {
            if (value == null) return '';
            const stringValue = String(value);

            if (
                stringValue.includes(',') ||
                stringValue.includes('"') ||
                stringValue.includes('\n')
            ) {
                return `"${stringValue.replace(/"/g, '""')}"`;
            }

            return stringValue;
        };

        let csvStr = '';

        const headers = i18n.t('utils.reportData.detailHeaders');
        csvStr += headers + '\n';

        data.forEach((r) => {
            const row = [
                r.name,
                r.email,
                r.age,
                r.group,
                r.tags,
                r.mskScore,
                r.mskRisk,
                r.pain,
                r.focus1,
                r.focus2
            ]
                .map(escapeCsv)
                .join(',');

            csvStr += row + '\n';
        });

        return csvStr;
    }

    public static generateScore(data: DetailUserRow[]) {
        const result: ScoreRow[] = [];

        data.forEach((u) => {
            const row: ScoreRow = {
                name: u.name,
                email: u.email,
                age: u.dob ? this.getAgeFromDob(u.dob) : '',
                group: u.groupName ?? '',
                tags: this.stringTags(u.tags),
                movementScore: u.movementScore ?? '',
                breathingScore: u.breathingScore ?? '',
                injuryScore: u.injuryHistoryScore ?? '',
                behavioralScore: u.behavioralScore ?? '',
                activityScore: u.activityScore ?? '',
                bodyCompScore: u.bodyCompScore ?? '',
                sleepScore: u.sleepScore ?? '',
                nutritionScore: u.nutritionScore ?? '',
                mskScore: u.mskScore ?? '',
            };

            // if (u.tags && u.tags.length > 0) {
            //     let tagString = '';
            //     u.tags.forEach((t: string, i) => tagString.concat(t, i !== tags.length ? ' - ' : ''));
            //     row.tags = tagString;
            // }

            result.push(row);
        })

        return this.generateScoreCsv(result);
    }

    private static generateScoreCsv(data: ScoreRow[]) {
        let csvStr = '';
        const headers = i18n.t('utils.reportData.scoreHeaders') + '\n'
        csvStr += headers;
        data.forEach((r) => {
            let row = '';
            row += r.name + ',';
            row += r.email + ',';
            row += r.age + ',';
            row += r.group + ',';
            row += r.tags + ',';
            row += r.movementScore + ',';
            row += r.breathingScore + ',';
            row += r.injuryScore + ',';
            row += r.behavioralScore + ',';
            row += r.activityScore + ',';
            row += r.bodyCompScore + ',';
            row += r.sleepScore + ',';
            row += r.nutritionScore + ',';
            row += r.mskScore + '\n';
            csvStr += row;
        });
        return csvStr;
    }

    public static generateSummary(data: ReportSummaryData) {
        const row: SummaryRow = {
            organization: data.organizationName ?? '',
            month: data.reportDate ? this.getMonthString(data.reportDate) : '',
            users: data.totalUsers,
            currentMsk: '',
            month8: '',
            month7: '',
            month6: '',
            month5: '',
            month4: '',
            month3: '',
            month2: '',
            month1: '',
            scores0to9: data.scoreBreakdown ? data.scoreBreakdown[0].count : '',
            scores10to19: data.scoreBreakdown ? data.scoreBreakdown[1].count : '',
            scores20to29: data.scoreBreakdown ? data.scoreBreakdown[2].count : '',
            scores30to39: data.scoreBreakdown ? data.scoreBreakdown[3].count : '',
            scores40to49: data.scoreBreakdown ? data.scoreBreakdown[4].count : '',
            scores50to59: data.scoreBreakdown ? data.scoreBreakdown[5].count : '',
            scores60to69: data.scoreBreakdown ? data.scoreBreakdown[6].count : '',
            scores70to79: data.scoreBreakdown ? data.scoreBreakdown[7].count : '',
            scores80to89: data.scoreBreakdown ? data.scoreBreakdown[8].count : '',
            scores90plus: data.scoreBreakdown ? data.scoreBreakdown[9].count : '',
            highRisk: data.riskFactorCodeCounts[0].count,
            moderateRisk: data.riskFactorCodeCounts[1].count,
            slightRisk: data.riskFactorCodeCounts[2].count,
            lowRisk: data.riskFactorCodeCounts[3].count,
            movementHealth: data.focusAreas ? data.focusAreas.find((x) => x.groupId === GroupIds.MOVEMENT_HEALTH)?.count ?? 0 : '',
            breathingQuality: data.focusAreas ? data.focusAreas.find((x) => x.groupId === GroupIds.BREATHING)?.count ?? 0 : '',
            injuryHistory: data.focusAreas ? data.focusAreas.find((x) => x.groupId === GroupIds.INJURY_HISTORY)?.count ?? 0 : '',
            behavioralHealth: data.focusAreas ? data.focusAreas.find((x) => x.groupId === GroupIds.BEHAVIORAL_HEALTH)?.count ?? 0 : '',
            physicalActivity: data.focusAreas ? data.focusAreas.find((x) => x.groupId === GroupIds.PHYSICAL_ACTIVITY)?.count ?? 0 : '',
            bodyComposition: data.focusAreas ? data.focusAreas.find((x) => x.groupId === GroupIds.BODY_COMPOSITION)?.count ?? 0 : '',
            sleepQuality: data.focusAreas ? data.focusAreas.find((x) => x.groupId === GroupIds.SLEEP)?.count ?? 0 : '',
            nutritionalAwareness: data.focusAreas ? data.focusAreas.find((x) => x.groupId === GroupIds.NUTRITION)?.count ?? 0 : '',
            toeTouch: data.movementFocusAreas ? data.movementFocusAreas.find((x) => x.groupId === GroupIds.TOE_TOUCH)?.count ?? 0 : '',
            balanceAndReach: data.movementFocusAreas ? data.movementFocusAreas.find((x) => x.groupId === GroupIds.BALANCE)?.count ?? 0 : '',
            shoulderMobility: data.movementFocusAreas ? data.movementFocusAreas.find((x) => x.groupId === GroupIds.SHOULDER_MOBILITY)?.count ?? 0 : '',
            shoulderClearing: data.movementFocusAreas ? data.movementFocusAreas.find((x) => x.groupId === GroupIds.SHOULDER_CLEARING)?.count ?? 0 : '',
            rotation: data.movementFocusAreas ? data.movementFocusAreas.find((x) => x.groupId === GroupIds.ROTATION)?.count ?? 0 : '',
            spineClearing: data.movementFocusAreas ? data.movementFocusAreas.find((x) => x.groupId === GroupIds.SPINE_CLEARING)?.count ?? 0 : '',
            squat: data.movementFocusAreas ? data.movementFocusAreas.find((x) => x.groupId === GroupIds.SQUAT)?.count ?? 0 : '',
            pain: data.movementFocusAreas ? data.movementFocusAreas.find((x) => x.groupId === GroupIds.PAIN)?.count ?? 0 : '',
        }

        if (data.history && data.history.length > 0) {
            const len = data.history.length;
            row.currentMsk = data.history[0].percent;
            if (len > 1) row.month8 = data.history[1].percent === -1 ? '' : data.history[1].percent;
            if (len > 2) row.month7 = data.history[2].percent === -1 ? '' : data.history[2].percent;
            if (len > 3) row.month6 = data.history[3].percent === -1 ? '' : data.history[3].percent;
            if (len > 4) row.month5 = data.history[4].percent === -1 ? '' : data.history[4].percent;
            if (len > 5) row.month4 = data.history[5].percent === -1 ? '' : data.history[5].percent;
            if (len > 6) row.month3 = data.history[6].percent === -1 ? '' : data.history[6].percent;
            if (len > 7) row.month2 = data.history[7].percent === -1 ? '' : data.history[7].percent;
            if (len > 8) row.month1 = data.history[8].percent === -1 ? '' : data.history[8].percent;
        }

        return this.generateSummaryCsv([row]);
    }

    private static generateSummaryCsv(data: SummaryRow[]) {
        let csvStr = '';
        const headers = i18n.t('utils.reportData.summaryHeaders') + '\n'
        csvStr += headers;
        data.forEach((r) => {
            let row = '';
            row += r.organization + ',';
            row += r.month + ',';
            row += r.users + ',';
            row += r.currentMsk + ',';
            row += r.month8 + ',';
            row += r.month7 + ',';
            row += r.month6 + ',';
            row += r.month5 + ',';
            row += r.month4 + ',';
            row += r.month3 + ',';
            row += r.month2 + ',';
            row += r.month1 + ',';
            row += r.scores0to9 + ',';
            row += r.scores10to19 + ',';
            row += r.scores20to29 + ',';
            row += r.scores30to39 + ',';
            row += r.scores40to49 + ',';
            row += r.scores50to59 + ',';
            row += r.scores60to69 + ',';
            row += r.scores70to79 + ',';
            row += r.scores80to89 + ',';
            row += r.scores90plus + ',';
            row += r.highRisk + ',';
            row += r.moderateRisk + ',';
            row += r.slightRisk + ',';
            row += r.lowRisk + ',';
            row += r.movementHealth + ',';
            row += r.breathingQuality + ',';
            row += r.injuryHistory + ',';
            row += r.behavioralHealth + ',';
            row += r.physicalActivity + ',';
            row += r.bodyComposition + ',';
            row += r.sleepQuality + ',';
            row += r.nutritionalAwareness + ',';
            row += r.toeTouch + ',';
            row += r.balanceAndReach + ',';
            row += r.shoulderMobility + ',';
            row += r.shoulderClearing + ',';
            row += r.rotation + ',';
            row += r.spineClearing + ',';
            row += r.squat + ',';
            row += r.pain + '\n';
            csvStr += row;
        });
        return csvStr;
    }

    public static generateMovement(data: ReportMovementData) {
        const result: MovementRow[] = [];

        data.users.forEach((u) => {
            const userTags = this.stringTags(u.tags);
            const row: MovementRow = {
                name: u.name,
                email: u.email,
                age: u.dob ? this.getAgeFromDob(u.dob) : '',
                group: u.groupName ?? '',
                tags: userTags,
                toeTouch: u.toeTouchScore ?? '',
                shoulderMobility: u.shoulderMobilityScore ?? '',
                rotation: u.rotationScore ?? '',
                balance: u.balanceScore ?? '',
                squat: u.squatScore ?? '',
                shoulderClearing: u.shoulderClearing ? i18n.t('general.pass') : i18n.t('general.fail'),
                spineClearing: u.spineClearing ? i18n.t('general.pass') : i18n.t('general.fail'),
                total: u.movementScore ?? '',
                focus: u.movementFocus ?? '',
            };

            result.push(row);
        });

        return this.generateMovementCsv(result);
    }

    private static generateMovementCsv(data: MovementRow[]) {
        let csvStr = '';
        const headers = i18n.t('utils.reportData.movementHeaders') + '\n';
        csvStr += headers;
        data.forEach((r) => {
            let row = '';
            row += r.name + ',';
            row += r.email + ',';
            row += r.age + ',';
            row += r.group + ',';
            row += r.tags + ',';
            row += r.toeTouch + ',';
            row += r.shoulderMobility + ',';
            row += r.rotation + ',';
            row += r.balance + ',';
            row += r.squat + ',';
            row += r.shoulderClearing + ',';
            row += r.spineClearing + ',';
            row += r.total + ',';
            row += r.focus + '\n';
            csvStr += row;
        });
        return csvStr;
    }

    private static async generateWellnessReport(userId: string, organizationId: string, type: number, mskScore?: MskScore) {
        const date = DateTime.now().toISODate();

        const report: Report = {
            dateEnd: date,
            dateStart: date,
            emailResults: false,
            organizationId: organizationId,
            status: "scheduled",
            type: type,
            userId: userId,
            mskScoreId: mskScore ? mskScore.id : undefined,
            language: i18n.language,
        };

        try {
            const user = await UserService.get(userId);

            const scheduledReport = await ReportService.save(
                report,
                user
            );

            if (!scheduledReport.id) {
                log.error("Wellness report scheduled without an id", {
                    userId,
                    organizationId: report.organizationId,
                    reportType: report.type,
                });
                return;
            }

            const reportUnsubscribe = ReportService.getSnapshot(
                scheduledReport.id,
                (r: Report) => {
                    if (r.status === "complete" && r.url) {
                        reportUnsubscribe();

                        //Loads in the System browser
                        window.open(r.url);
                        log.info("Report downloaded", {
                            reportId: scheduledReport.id,
                            reportType: report.type,
                            organizationId: report.organizationId,
                        });
                    }
                    if (r.status === "error") {
                        reportUnsubscribe();
                        log.error("Report generation failed", {
                            reportId: scheduledReport.id,
                            reportType: report.type,
                            organizationId: report.organizationId,
                        });
                    }
                }
            );
        } catch (e) {
            log.error("Failed to generate wellness report", {
                exception: e,
                userId,
                organizationId: report.organizationId,
                reportType: report.type,
            });
        }
    }

    public static async downloadDetailOrScore(users: DetailUserRow[], filters: DetailReportFilterState, organizationId: string, organizationName: string, userId: string, reportMonth: string, type: ReportType) {

        const report: Report = {
            dateStart: DateTime.now().toISODate(),
            dateEnd: DateTime.now().toISODate(),
            organizationId: organizationId,
            status: "scheduled",
            type: type,
            userId: userId,
            language: i18n.language,
        }

        const reportData: ReportDetailData = {
            focusAreas: filters.focusAreas,
            groupNames: filters.groupNames ?? [],
            healthCategories: filters.healthCategories.map(value => {
                const option = MskHealthRiskOptions.find(o => o.value === value);
                return option?.label ?? value;
            }),
            isInPain: filters.inPain,
            organizationName: organizationName ?? '',
            reportDate: reportMonth,
            tagNames: filters.tagNames ?? [],
            filterNames: this.buildFilterNames(filters.inPain, filters.healthCategories, filters.focusAreas),
            totalUsers: users.length,
            users: users.map((u) => ({
                dob: u.dob,
                email: u.email,
                focusAreas: u.focusAreas,
                groupName: u.groupName,
                healthCategory: u.healthCategory,
                inPain: u.inPain,
                mskScore: u.mskScore,
                name: u.name,
                movementScore: u.movementScore,
                bodyCompScore: u.bodyCompScore,
                activityScore: u.activityScore,
                injuryHistoryScore: u.injuryHistoryScore,
                breathingScore: u.breathingScore,
                behavioralScore: u.behavioralScore,
                sleepScore: u.sleepScore,
                nutritionScore: u.nutritionScore,
                tags: u.tags,
            })),
        };
        try {
            let downloadSnackbar: SnackbarKey;
            downloadSnackbar = enqueueSnackbar(i18n.t('general.reportDownload.pleaseWait').toString(), { variant: "info", autoHideDuration: 30000 });

            // Save report object
            const savedReport = await ReportService.save(report)

            if (!savedReport.id) {
                log.warn("Report download blocked: report saved without an id", {
                    organizationId,
                    reportType: type,
                });
                return
            }

            // Save report data object
            await ReportDataService.addDetailData(reportData, savedReport.id);

            // once both objects are saved, call our firebase function
            FunctionService.generateReportV2(savedReport);

            const reportUnsubscribe = ReportService.getSnapshot(
                savedReport.id,
                (r: Report) => {
                    if (r.status === "complete" && r.url) {
                        reportUnsubscribe();

                        //Loads in the System browser
                        closeSnackbar(downloadSnackbar);
                        window.open(r.url);
                        log.info("Report downloaded", {
                            reportId: savedReport.id,
                            reportType: type,
                            organizationId,
                            reportMonth,
                        });
                    }
                    if (r.status === "error") {
                        closeSnackbar(downloadSnackbar);
                        reportUnsubscribe();
                        log.error("Report generation failed during download", {
                            reportId: savedReport.id,
                            reportType: type,
                            organizationId,
                        });
                    }
                }
            );

        }
        catch (e) {
            log.error("Failed to download report", {
                exception: e,
                reportType: type,
                organizationId,
                reportMonth,
            });
        }

    }

    public static async downloadMovement(users: ReportUserMovementItem[], filters: DetailReportFilterState, organizationId: string, organizationName: string, userId: string, reportMonth: string) {

        const report: Report = {
            dateStart: DateTime.now().toISODate(),
            dateEnd: DateTime.now().toISODate(),
            organizationId: organizationId,
            status: "scheduled",
            type: ReportType.Movement,
            userId: userId,
            language: i18n.language,
        }

        const reportData: ReportMovementData = {
            focusAreas: filters.focusAreas,
            groupNames: filters.groupNames ?? [],
            healthCategories: filters.healthCategories.map(value => {
                const option = MskHealthRiskOptions.find(o => o.value === value);
                return option?.label ?? value;
            }),
            isInPain: filters.inPain,
            organizationName: organizationName,
            reportDate: reportMonth,
            tagNames: filters.tagNames ?? [],
            filterNames: this.buildFilterNames(filters.inPain, filters.healthCategories, filters.focusAreas),
            totalUsers: users.length,
            users: users
        };
        try {
            let downloadSnackbar: SnackbarKey;
            downloadSnackbar = enqueueSnackbar(i18n.t('general.reportDownload.pleaseWait').toString(), { variant: "info", autoHideDuration: 30000 });

            // Save report object
            const savedReport = await ReportService.save(report)

            if (!savedReport.id) {
                log.warn("Report download blocked: report saved without an id", {
                    organizationId,
                    reportType: ReportType.Movement,
                });
                return
            }

            // Save report data object
            await ReportDataService.addMovementData(reportData, savedReport.id);

            // once both objects are saved, call our firebase function
            FunctionService.generateReportV2(savedReport);

            const reportUnsubscribe = ReportService.getSnapshot(
                savedReport.id,
                (r: Report) => {
                    if (r.status === "complete" && r.url) {
                        reportUnsubscribe();

                        //Loads in the System browser
                        closeSnackbar(downloadSnackbar);
                        window.open(r.url);
                        log.info("Report downloaded", {
                            reportId: savedReport.id,
                            reportType: ReportType.Movement,
                            organizationId,
                            reportMonth,
                        });
                    }
                    if (r.status === "error") {
                        closeSnackbar(downloadSnackbar);
                        reportUnsubscribe();
                        log.error("Report generation failed during download", {
                            reportId: savedReport.id,
                            reportType: ReportType.Movement,
                            organizationId,
                        });
                    }
                }
            );

        }
        catch (e) {
            log.error("Failed to download report", {
                exception: e,
                reportType: ReportType.Movement,
                organizationId,
                reportMonth,
            });
        }

    }

    public static async downloadSummary(summaryData: ReportSummaryData, filters: ReportFilterState, organizationId: string, organizationName: string, userId: string, reportMonth: string) {

        const report: Report = {
            dateStart: DateTime.now().startOf('month').toISODate(),
            dateEnd: DateTime.now().startOf('month').toISODate(),
            organizationId: organizationId,
            status: "scheduled",
            type: ReportType.Summary,
            userId: userId,
            language: i18n.language,
        }

        const reportData: ReportSummaryData = {
            groupNames: summaryData?.groupNames,
            tagNames: summaryData?.tagNames,
            focusAreas: summaryData.focusAreas,
            history: summaryData.history,
            organizationName: summaryData.organizationName,
            pendingUsers: summaryData.pendingUsers,
            riskFactorCodeCounts: summaryData.riskFactorCodeCounts,
            scoreBreakdown: summaryData.scoreBreakdown,
            movementFocusAreas: summaryData.movementFocusAreas,
            totalUsers: summaryData.totalUsers,
            reportDate: summaryData.reportDate
        };
        try {
            let downloadSnackbar: SnackbarKey;
            downloadSnackbar = enqueueSnackbar(i18n.t('general.reportDownload.pleaseWait').toString(), { variant: "info", autoHideDuration: 30000 });

            // Save report object
            const savedReport = await ReportService.save(report)

            if (!savedReport.id) {
                log.warn("Report download blocked: report saved without an id", {
                    organizationId,
                    reportType: ReportType.Summary,
                });
                return
            }

            // Save report data object
            await ReportDataService.addSummaryData(reportData, savedReport.id);

            // once both objects are saved, call our firebase function
            FunctionService.generateReportV2(savedReport);

            const reportUnsubscribe = ReportService.getSnapshot(
                savedReport.id,
                (r: Report) => {
                    if (r.status === "complete" && r.url) {
                        reportUnsubscribe();

                        //Loads in the System browser
                        closeSnackbar(downloadSnackbar);
                        window.open(r.url);
                        log.info("Report downloaded", {
                            reportId: savedReport.id,
                            reportType: ReportType.Summary,
                            organizationId,
                            reportMonth,
                        });
                    }
                    if (r.status === "error") {
                        closeSnackbar(downloadSnackbar);
                        reportUnsubscribe();
                        log.error("Report generation failed during download", {
                            reportId: savedReport.id,
                            reportType: ReportType.Summary,
                            organizationId,
                        });
                    }
                }
            );

        }
        catch (e) {
            log.error("Failed to download report", {
                exception: e,
                reportType: ReportType.Summary,
                organizationId,
                reportMonth,
            });
        }

    }

    public static async downloadCompliance(dynamicReportData: ReportDynamicComplianceData, filters: ReportFilterState, organizationId: string, userId: string) {
        const report: Report = {
            dateStart: DateTime.now().toISODate(),
            dateEnd: DateTime.now().toISODate(),
            organizationId: organizationId,
            status: "scheduled",
            type: ReportType.Compliance,
            userId: userId,
            language: i18n.language,
        }

        const reportData: ReportComplianceData = {
            groupNames: filters.groupNames ?? [],
            organizationName: dynamicReportData.organizationName,
            pendingAssessmentUsers: dynamicReportData.pendingAssessmentUsers?.map(u => u.displayText) ?? [],
            pendingLoginUsers: dynamicReportData.pendingLoginUsers?.map(u => u.displayText) ?? [],
            pendingRetest: dynamicReportData.pendingRetest?.map(u => u.displayText) ?? [],
            pendingUsers: dynamicReportData.pendingUsers ?? 0,
            reportDate: report.dateEnd,
            tagNames: filters.tagNames ?? [],
            totalUsers: dynamicReportData.totalUsers ?? 0,
            usersAssessment: dynamicReportData.usersAssessment ?? 0,
            usersCompletedTasks: dynamicReportData.usersCompletedTasks ?? 0,
            usersLoggedIn: dynamicReportData.usersLoggedIn ?? 0,
        };
        try {
            let downloadSnackbar: SnackbarKey;
            downloadSnackbar = enqueueSnackbar(i18n.t('general.reportDownload.pleaseWait').toString(), { variant: "info", autoHideDuration: 30000 });

            // Save report object
            const savedReport = await ReportService.save(report)

            if (!savedReport.id) {
                log.warn("Report download blocked: report saved without an id", {
                    organizationId,
                    reportType: ReportType.Compliance,
                });
                return
            }

            // Save report data object
            await ReportDataService.addComplianceData(reportData, savedReport.id);

            // once both objects are saved, call our firebase function
            FunctionService.generateReportV2(savedReport);

            const reportUnsubscribe = ReportService.getSnapshot(
                savedReport.id,
                (r: Report) => {
                    if (r.status === "complete" && r.url) {
                        reportUnsubscribe();

                        //Loads in the System browser
                        closeSnackbar(downloadSnackbar);
                        window.open(r.url);
                        log.info("Report downloaded", {
                            reportId: savedReport.id,
                            reportType: ReportType.Compliance,
                            organizationId,
                        });
                    }
                    if (r.status === "error") {
                        closeSnackbar(downloadSnackbar);
                        reportUnsubscribe();
                        log.error("Report generation failed during download", {
                            reportId: savedReport.id,
                            reportType: ReportType.Compliance,
                            organizationId,
                        });
                    }
                }
            );

        }
        catch (e) {
            log.error("Failed to download report", {
                exception: e,
                reportType: ReportType.Compliance,
                organizationId,
            });
        }

    }

    public static async downloadWellness(user: User | null | undefined, mskScore: MskScore | null | undefined) {
        if (!user || !user.id || !mskScore) return;
        const userInFirebase = await UserService.get(user.id);
        if (!userInFirebase || !userInFirebase.organizationId) return;
        const latestOrgId = userInFirebase.organizationId;
        const userId = userInFirebase.id;

        if (!userId) {
            return;
        }
        // Get assessmentResponses
        const assessmentResponseCheck = await AssessmentUtils.fetchAssessmentResponsesById(userId)
        // Get latestMskScore
        const mskScoreVersion = MskScoreUtil.baseCheckMskScore(mskScore);
        let type = ReportType.Assessment
        if ((assessmentResponseCheck && assessmentResponseCheck.length > 7) || mskScoreVersion) { // v == 0 isn't wellness, it's old report
            type = ReportType.Wellness
        }
        const date = DateTime.utc().toISO();
        const report = await ReportService.getBy(
            [
                {
                    field: "userId",
                    operator: "==",
                    value: userId,
                },
                {
                    field: "organizationId",
                    operator: "==",
                    value: latestOrgId,
                },
                {
                    field: "status",
                    operator: "==",
                    value: "complete",
                },
                {
                    field: "type",
                    operator: "==",
                    value: type,
                },
                {
                    field: "expiration",
                    operator: ">",
                    value: date,
                },
                {
                    field: "mskScoreId",
                    operator: "==",
                    value: mskScore.id
                }
            ], [
            {
                field: "expiration",
                direction: "desc",
            },
            {
                field: "updated",
                direction: "desc",
            }]);

        let downloadSnackbar: SnackbarKey;

        if (report.length <= 0 ||
            (
                mskScore.updated &&
                report[0].created &&
                report[0].created.toMillis() < mskScore.updated.toMillis()
            )) {
            downloadSnackbar = enqueueSnackbar(i18n.t('general.reportDownload.pleaseWait').toString(), { variant: "info", autoHideDuration: 30000 });

            if (mskScoreVersion > 0) { // V1 and greater use mskScore for generating data
                this.generateWellnessReport(userId, latestOrgId, type, mskScore);
            }
            else { // V0 uses assessment responses only
                this.generateWellnessReport(userId, latestOrgId, type);
            }
            return;
        }

        if (!report[0].id) {
            log.warn("Wellness report download blocked: no report id", {
                organizationId: latestOrgId,
                reportType: type,
            });
            return;
        }

        log.info("Report downloaded (cached)", {
            reportId: report[0].id,
            reportType: type,
            organizationId: latestOrgId,
        });

        const reportUnsubscribe = ReportService.getSnapshot(
            report[0].id,
            (r: Report) => {
                if (r.status === "complete" && r.url) {
                    reportUnsubscribe();

                    //Loads in the System browser
                    closeSnackbar(downloadSnackbar);
                    window.open(r.url);
                }
                if (r.status === "error") {
                    closeSnackbar(downloadSnackbar);
                    reportUnsubscribe();
                    log.error("Report generation failed during download", {
                        reportId: report[0].id,
                        reportType: type,
                        organizationId: latestOrgId,
                    });
                }
            }
        );
    }

    public static async downloadWellnessSummary(dynamicReportData: ReportWellnessSummaryData, filters: ReportFilterState, organizationId: string, userId: string) {

        const report: Report = {
            dateStart: DateTime.now().toISODate(),
            dateEnd: DateTime.now().toISODate(),
            organizationId: organizationId,
            status: "scheduled",
            type: ReportType.WellnessSummary,
            userId: userId,
            language: i18n.language,
        }

        const reportData: ReportWellnessSummaryData = {
            tagNames: filters.tagNames ?? [],
            groupNames: filters.groupNames ?? [],
            averageEngagement: dynamicReportData.averageEngagement ?? -1,
            sevenDayEngagement: dynamicReportData.sevenDayEngagement ?? -1,
            thirtyDayEngagement: dynamicReportData.thirtyDayEngagement ?? -1,
            averageDaysMissed: dynamicReportData.averageDaysMissed ?? -1,
            averageStreak: dynamicReportData.averageStreak ?? -1,
            activePrograms: dynamicReportData.activePrograms ?? -1,
            pendingRetest: dynamicReportData.pendingRetest ?? -1,
            enrolledUsers: dynamicReportData.enrolledUsers ?? -1,
            userData: dynamicReportData.userData ?? [],
            organizationName: dynamicReportData.organizationName ?? "",
            reportDate: DateTime.now().toISODate(), // TODO translate
            totalUsers: dynamicReportData.totalUsers ?? -1,
        };
        try {
            let downloadSnackbar: SnackbarKey;
            downloadSnackbar = enqueueSnackbar(i18n.t('general.reportDownload.pleaseWait').toString(), { variant: "info", autoHideDuration: 30000 });

            // Save report object
            const savedReport = await ReportService.save(report)

            if (!savedReport.id) {
                log.warn("Report download blocked: report saved without an id", {
                    organizationId,
                    reportType: ReportType.WellnessSummary,
                });
                return
            }

            // Save report data object
            await ReportDataService.addWellnessSummaryData(reportData, savedReport.id);

            // once both objects are saved, call our firebase function
            FunctionService.generateReportV2(savedReport);

            const reportUnsubscribe = ReportService.getSnapshot(
                savedReport.id,
                (r: Report) => {
                    if (r.status === "complete" && r.url) {
                        reportUnsubscribe();

                        //Loads in the System browser
                        closeSnackbar(downloadSnackbar);
                        window.open(r.url);
                        log.info("Report downloaded", {
                            reportId: savedReport.id,
                            reportType: ReportType.WellnessSummary,
                            organizationId,
                        });
                    }
                    if (r.status === "error") {
                        closeSnackbar(downloadSnackbar);
                        reportUnsubscribe();
                        log.error("Report generation failed during download", {
                            reportId: savedReport.id,
                            reportType: ReportType.WellnessSummary,
                            organizationId,
                        });
                    }
                }
            );

        }
        catch (e) {
            log.error("Failed to download report", {
                exception: e,
                reportType: ReportType.WellnessSummary,
                organizationId,
            });
        }

    }

    public static generateWellnessSummary(data: ReportWellnessSummaryData) {
        const result: WellnessSummaryRow[] = [];
        result.push({
            group: data.groupNames?.join(", ") ?? '',
            tags: data.tagNames?.join(", ") ?? '',
            users: data.totalUsers,
            averageEngagement: data.averageEngagement ?? 0,
            sevenDayEngagement: data.sevenDayEngagement ?? 0,
            thirtyDayEngagement: data.thirtyDayEngagement ?? 0,
            averageDaysMissed: data.averageDaysMissed ?? 0,
            averageStreak: data.averageStreak ?? 0,
            activePrograms: data.activePrograms ?? 0,
            pendingRetest: data.pendingRetest ?? 0,
            enrolledUsers: data.enrolledUsers ?? 0,
            name: '',
            programStatus: '',
            daysLeft: '',
            daysMissed: '',
            currentStreak: '',
            userSevenDayEngagement: '',
            userThirtyDayEngagement: '',
            progressPercentage: '',

        })

        const emptyRow = {
            group: '',
            tags: '',
            users: '',
            averageEngagement: '',
            sevenDayEngagement: '',
            thirtyDayEngagement: '',
            averageDaysMissed: '',
            averageStreak: '',
            activePrograms: '',
            pendingRetest: '',
            enrolledUsers: '',
            name: '',
            programStatus: '',
            daysLeft: '',
            daysMissed: '',
            currentStreak: '',
            userSevenDayEngagement: '',
            userThirtyDayEngagement: '',
            progressPercentage: '',
        }

        data.userData.forEach((u, i) => {
            if (result.length >= i + 1) {
                result[i].name = u.name;
                result[i].programStatus = u.programStatus !== null && u.programStatus !== undefined ? i18n.t(WellnessProgramStatusTranslation[u.programStatus]).toString() : '';
                result[i].daysLeft = u.daysLeft ?? '';
                result[i].daysMissed = u.daysMissed ?? '';
                result[i].currentStreak = u.currentStreak ?? '';
                result[i].userSevenDayEngagement = u.sevenDayEngagement ?? '';
                result[i].userThirtyDayEngagement = u.thirtyDayEngagement ?? '';
                result[i].progressPercentage = u.progressPercentage ?? '';
            }
            else {
                const newRow: WellnessSummaryRow = { ...emptyRow };
                newRow.name = u.name;
                newRow.programStatus = u.programStatus !== null && u.programStatus !== undefined ? i18n.t(WellnessProgramStatusTranslation[u.programStatus]).toString() : '';
                newRow.daysLeft = u.daysLeft ?? '';
                newRow.daysMissed = u.daysMissed ?? '';
                newRow.currentStreak = u.currentStreak ?? '';
                newRow.userSevenDayEngagement = u.sevenDayEngagement ?? '';
                newRow.userThirtyDayEngagement = u.thirtyDayEngagement ?? '';
                newRow.progressPercentage = u.progressPercentage ?? '';
                result.push(newRow);
            }
        });

        return this.generateWellnessSummaryCsv(result);
    }

    private static generateWellnessSummaryCsv(data: WellnessSummaryRow[]) {
        let csvStr = '';
        const headers = i18n.t('utils.reportData.wellnessSummaryHeaders') + '\n';
        csvStr += headers;
        data.forEach((r) => {
            let row = '';
            row += r.group + ',';
            row += r.tags + ',';
            row += r.users + ',';
            row += r.averageEngagement + ',';
            row += r.sevenDayEngagement + ',';
            row += r.thirtyDayEngagement + ',';
            row += r.averageDaysMissed + ',';
            row += r.averageStreak + ',';
            row += r.activePrograms + ',';
            row += r.pendingRetest + ',';
            row += r.enrolledUsers + ',';
            row += ' ,';
            row += r.name + ',';
            row += r.programStatus + ',';
            row += r.daysLeft + ',';
            row += r.daysMissed + ',';
            row += r.currentStreak + ',';
            row += r.userSevenDayEngagement + ',';
            row += r.userThirtyDayEngagement + ',';
            row += r.progressPercentage + '\n';
            csvStr += row;
        });
        return csvStr;
    }

    private static getAgeFromDob(dob: string) {
        const today = new Date();
        const dobDate = new Date(dob);
        let age = today.getFullYear() - dobDate.getFullYear();
        const m = today.getMonth() - dobDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
            age -= 1;
        }
        return age;
    }

    private static getMonthString(date: string) {
        const m = new Date(date).getMonth();

        const months = [
            i18n.t('utils.months.january'),
            i18n.t('utils.months.february'),
            i18n.t('utils.months.march'),
            i18n.t('utils.months.april'),
            i18n.t('utils.months.may'),
            i18n.t('utils.months.june'),
            i18n.t('utils.months.july'),
            i18n.t('utils.months.august'),
            i18n.t('utils.months.september'),
            i18n.t('utils.months.october'),
            i18n.t('utils.months.november'),
            i18n.t('utils.months.december'),
        ];

        return months[m + 1];
    }

    private static stringTags(tags: UserTag[]) {
        if (!tags || tags.length < 1) {
            return '';
        }

        let tagStr = '';

        tags.forEach((t, i) => {
            if (i + 1 < tags.length) {
                tagStr += `${t.name} / `;
            }
            tagStr += t.name;
        });

        return tagStr;
    }

    public static buildReportFilterPayload = (groups: Group[], tags: Tag[], filters: FilterState): ReportFilterState => {
        return {
            groupNames: groups
                .filter(g => filters.groupIds.includes(g.id))
                .map(g => g.name),

            tagNames: tags
                .filter(t => filters.tagIds.includes(t.id))
                .map(t => t.name),

        };
    };

    public static buildReportDetailFilterPayload = (groups: Group[], tags: Tag[], filters: DetailFilterState): DetailReportFilterState => {
        return {
            groupNames: groups
                .filter(g => filters.groupIds.includes(g.id))
                .map(g => g.name),

            tagNames: tags
                .filter(t => filters.tagIds.includes(t.id))
                .map(t => t.name),

            healthCategories: filters.healthCategories,
            focusAreas: filters.focusAreas,
            inPain: filters.inPain === PainFilterOption.YES ? true : filters.inPain === PainFilterOption.NO ? false : null
        };
    };

    public static async loadGroupsAndTags(
        organizationId: string
    ): Promise<{ groups: Group[]; tags: Tag[] }> {

        if (!organizationId) {
            return { groups: [], tags: [] };
        }

        const [groups, tags] = await Promise.all([
            GroupService.getBy([
                { field: "organizationId", operator: "==", value: organizationId }
            ]),
            TagService.getBy([
                { field: "organizationId", operator: "==", value: organizationId }
            ])
        ]);

        return { groups, tags };
    }

    public static grabInitialDetailData = async (
        organizationId: string,
        selectedReportMonth: string
    ): Promise<ReportDynamicDetailData> => {

        const result = await this.grabInitialDetailUserData(
            organizationId,
            selectedReportMonth
        );

        const counts = this.calculateDetailCounts(result.users);

        return {
            users: result.users,
            organizationName: result.organizationName,
            reportDate: selectedReportMonth ?? DateTime.now().startOf("month").toISODate(),
            totalUsers: result.users.length,
            ...counts,
        };
    };

    public static calculateDetailCounts(users: DetailUserRow[]) {
        let highRiskCount = 0;
        let moderateRiskCount = 0;
        let slightRiskCount = 0;
        let lowRiskCount = 0;
        let usersWithPainCount = 0;

        for (const user of users) {
            switch (user.healthCategory) {
                case MskHealthRiskValues.Health:
                    highRiskCount++;
                    break;
                case MskHealthRiskValues.Wellness:
                    moderateRiskCount++;
                    break;
                case MskHealthRiskValues.Fitness:
                    slightRiskCount++;
                    break;
                case MskHealthRiskValues.Performance:
                    lowRiskCount++;
                    break;
            }
            if (user.inPain) {
                usersWithPainCount++;
            }
        }

        return {
            highRiskCount,
            moderateRiskCount,
            slightRiskCount,
            lowRiskCount,
            usersWithPainCount,
        };
    }

    public static calculateScoreMetrics(users: DetailUserRow[]) {

        if (!users.length) {
            return {
                highRiskCount: 0,
                moderateRiskCount: 0,
                slightRiskCount: 0,
                lowRiskCount: 0,
                usersWithInjuryBelowSeventyCount: 0,
                usersWithThreeOrMoreDomainsBelowSeventyCount: 0,
                lowPerformingUserMskCount: 0,
                highPerformingUserMskCount: 0,
                avgWellnessScore: 0,
            };
        }

        let totalWellnessScore = 0;
        let wellnessCount = 0;
        let highRiskCount = 0;
        let moderateRiskCount = 0;
        let slightRiskCount = 0;
        let lowRiskCount = 0;
        let usersWithInjuryBelowSeventyCount = 0;
        let usersWithThreeOrMoreDomainsBelowSeventyCount = 0;
        let lowPerformingUserMskCount = 0;
        let highPerformingUserMskCount = 0;

        for (const user of users) {

            // ---- Wellness Avg + Lowest ----
            if (user.mskScore != null) {
                totalWellnessScore += user.mskScore;
                wellnessCount++;
            }
        }

        const avgWellnessScore =
            wellnessCount ? totalWellnessScore / wellnessCount : 0;

        for (const user of users) {
            switch (user.healthCategory) {
                case MskHealthRiskValues.Health:
                    highRiskCount++;
                    break;
                case MskHealthRiskValues.Wellness:
                    moderateRiskCount++;
                    break;
                case MskHealthRiskValues.Fitness:
                    slightRiskCount++;
                    break;
                case MskHealthRiskValues.Performance:
                    lowRiskCount++;
                    break;
            }
            if (user.injuryHistoryScore != null && user.injuryHistoryScore < 70) {
                usersWithInjuryBelowSeventyCount++;
            }
            if (user.mskScore != null && user.mskScore < 50) {
                lowPerformingUserMskCount++;
            }
            if (user.mskScore != null && user.mskScore >= 85) {
                highPerformingUserMskCount++;
            }

            const domainScores = [
                user.movementScore,
                user.bodyCompScore,
                user.activityScore,
                user.injuryHistoryScore,
                user.breathingScore,
                user.sleepScore,
                user.nutritionScore,
                user.behavioralScore,
            ];

            const belowSeventyCount = domainScores.filter(
                (score) => score != null && score < 70
            ).length;

            if (belowSeventyCount >= 3) {
                usersWithThreeOrMoreDomainsBelowSeventyCount++;
            }
        }

        return {
            avgWellnessScore: Number(avgWellnessScore.toFixed(1)),
            highRiskCount,
            moderateRiskCount,
            slightRiskCount,
            lowRiskCount,
            usersWithInjuryBelowSeventyCount,
            usersWithThreeOrMoreDomainsBelowSeventyCount,
            lowPerformingUserMskCount,
            highPerformingUserMskCount,
        };
    }

    public static calculateMovementMetrics(users: ReportUserMovementItem[]) {
        const result = {
            toeTouchCount: 0,
            shoulderMobilityCount: 0,
            rotationCount: 0,
            squatCount: 0,
            balanceCount: 0,
            shoulderClearingCount: 0,
            spineClearingCount: 0,
            usersInPainCount: 0,
        };

        if (!users.length) {
            return result;
        }

        for (const user of users) {
            const focus = user.movementFocusValue;

            if (user.inPain) {
                result.usersInPainCount++;
            }

            if (
                focus === GroupIds.TOE_TOUCH ||
                focus === GroupIds.TOE_TOUCH_BREATHING
            ) {
                result.toeTouchCount++;
            }

            if (
                focus === GroupIds.SHOULDER_MOBILITY ||
                focus === GroupIds.SHOULDER_MOBILITY_BREATHING
            ) {
                result.shoulderMobilityCount++;
            }

            if (
                focus === GroupIds.ROTATION ||
                focus === GroupIds.ROTATION_BREATHING
            ) {
                result.rotationCount++;
            }

            if (
                focus === GroupIds.SQUAT ||
                focus === GroupIds.SQUAT_BREATHING
            ) {
                result.squatCount++;
            }

            if (
                focus === GroupIds.BALANCE ||
                focus === GroupIds.BALANCE_BREATHING
            ) {
                result.balanceCount++;
            }

            if (
                focus === GroupIds.SHOULDER_CLEARING ||
                focus === GroupIds.SHOULDER_CLEARING_BREATHING
            ) {
                result.shoulderClearingCount++;
            }

            if (
                focus === GroupIds.SPINE_CLEARING ||
                focus === GroupIds.SPINE_CLEARING_BREATHING
            ) {
                result.spineClearingCount++;
            }
        }

        return result;

    }

    public static grabInitialScoreData = async (
        organizationId: string,
        selectedReportMonth: string
    ): Promise<ReportDynamicScoreData> => {

        const result = await this.grabInitialDetailUserData(
            organizationId,
            selectedReportMonth
        );

        const metrics = this.calculateScoreMetrics(result.users);

        return {
            users: result.users,
            organizationName: result.organizationName,
            reportDate: selectedReportMonth ?? DateTime.now().startOf("month").toISODate(),
            totalUsers: result.users.length,
            ...metrics
        };
    };

    public static grabInitialDetailUserData = async (
        organizationId: string,
        selectedReportMonth: string
    ): Promise<{ users: DetailUserRow[]; organizationName: string }> => {



        const result = {
            users: [] as DetailUserRow[],
            organizationName: '',
        };

        /* ---------------- ORGANIZATION ---------------- */
        const organization = await OrganizationService.get(organizationId);

        if (!organization?.name) return result;

        result.organizationName = organization.name;

        /* ---------------- USERS ---------------- */
        const users = (await this.getReportUsers(organizationId))
            .filter((user) => user.authenticationId != null);


        if (users.length === 0) return result;

        /* ---------------- SCORES (PARALLEL) ---------------- */

        const startDate = DateTime.fromISO(selectedReportMonth);
        const isCurrentMonth = startDate >= DateTime.now().startOf("month");

        const batchPromises: Promise<MskScore[]>[] = [];

        for (let i = 0; i < Math.ceil(users.length / 10); i++) {
            const batchUserIds = users
                .slice(i * 10, (i + 1) * 10)
                .map(u => u.id);


            const promise = (
                isCurrentMonth
                    ? UserLatestMskScoreService.getBy([
                        { field: "userId", operator: "in", value: batchUserIds }
                    ])
                    : mskScoreMonthlyMskScoreService.getBy([
                        { field: "userId", operator: "in", value: batchUserIds },
                        { field: "date", operator: "==", value: startDate.startOf("month").toISODate() }
                    ])
            ).finally(() => {
            });

            batchPromises.push(promise);
        }

        // Run all batches in parallel
        const batchResults = await Promise.all(batchPromises);

        // Flatten results
        const scores = batchResults.flat();


        /* ---------------- FILTER + MAP ---------------- */

        result.users = users
            .filter((user) => {
                const mskScore = scores.find((s) => s.userId === user.id);
                return !!mskScore;
            })
            .map((user) => {
                const mskScore = scores.find((s) => s.userId === user.id);
                return {
                    id: user.id!,
                    groupId: user.groupId,
                    dob: user.birthdate ?? undefined,
                    email: user.email ?? '',
                    focusAreas: mskScore?.focusAreas?.map((area) => area.groupId!),
                    groupName: user.groupName,
                    healthCategory: this.getHealthCategory(mskScore?.riskFactor?.riskCode),
                    inPain: mskScore ? this.isInPain(mskScore) : false,
                    mskScore: mskScore?.percentage,
                    name: `${user.firstName} ${user.lastName}`,
                    movementScore: mskScore?.movementScore?.percentage,
                    bodyCompScore: mskScore?.lifestyleScore?.bodyCompositionScore?.percentage,
                    activityScore: mskScore?.lifestyleScore?.physicalActivityScore?.percentage,
                    injuryHistoryScore: mskScore?.lifestyleScore?.injuryHistoryScore?.percentage,
                    breathingScore: mskScore?.lifestyleScore?.breathingScore?.percentage,
                    sleepScore: mskScore?.lifestyleScore?.sleepScore?.percentage,
                    nutritionScore: mskScore?.lifestyleScore?.nutritionScore?.percentage,
                    behavioralScore: mskScore?.lifestyleScore?.behavioralScore?.percentage,
                    tags: user.tags ?? [],
                };
            })
            .filter(u => u.mskScore != null);



        return result;
    };

    public static grabInitialMovementData = async (
        organizationId: string,
        selectedReportMonth: string
    ): Promise<ReportDynamicMovementData> => {


        const result = {
            organizationName: '',
            reportDate: selectedReportMonth ?? DateTime.now().startOf("month").toISODate(),
            users: [] as MovementUserRow[],
            totalUsers: 0,
            toeTouchCount: 0,
            shoulderMobilityCount: 0,
            rotationCount: 0,
            squatCount: 0,
            balanceCount: 0,
            shoulderClearingCount: 0,
            spineClearingCount: 0,
            usersInPainCount: 0,
        };

        /* ---------------- ORGANIZATION ---------------- */
        const organization = await OrganizationService.get(organizationId);

        if (!organization?.name) return result;
        result.organizationName = organization.name;

        /* ---------------- USERS ---------------- */
        const users = (await this.getReportUsers(organizationId))
            .filter((user) => user.authenticationId != null);


        if (users.length === 0) return result;

        /* ---------------- SCORES (PARALLEL) ---------------- */

        const startDate = DateTime.fromISO(selectedReportMonth);
        const isCurrentMonth = startDate >= DateTime.now().startOf("month");

        const batchPromises: Promise<MskScore[]>[] = [];

        for (let i = 0; i < Math.ceil(users.length / 10); i++) {
            const batchUserIds = users
                .slice(i * 10, (i + 1) * 10)
                .map(u => u.id);


            const promise = (
                isCurrentMonth
                    ? UserLatestMskScoreService.getBy([
                        { field: "userId", operator: "in", value: batchUserIds }
                    ])
                    : mskScoreMonthlyMskScoreService.getBy([
                        { field: "userId", operator: "in", value: batchUserIds },
                        { field: "date", operator: "==", value: startDate.startOf("month").toISODate() }
                    ])
            ).finally(() => {
            });

            batchPromises.push(promise);
        }

        // Run all batches in parallel
        const batchResults = await Promise.all(batchPromises);

        // Flatten results
        const scores = batchResults.flat();


        /* ---------------- FILTER + MAP ---------------- */

        result.users = users
            .filter((user) => {
                const mskScore = scores.find((s) => s.userId === user.id);
                if (!mskScore || !mskScore.movementScore) {
                    return false
                }
                if (mskScore.movementScore.percentage == null) {
                    return false;
                }
                return true;
            })
            .map((user) => {
                const mskScore = scores.find((s) => s.userId === user.id);
                let movementFocus;
                if (mskScore?.movementScore && mskScore.movementScore.focusArea) {
                    movementFocus = MovementFocusOptionsWithBreathing.find((option) => option.value === mskScore.movementScore?.focusArea.groupId)
                }
                return {
                    id: user.id,
                    dob: user.birthdate ?? undefined,
                    inPain: mskScore ? this.isInPain(mskScore) : false,
                    mskScore: mskScore?.percentage,
                    name: `${user.firstName} ${user.lastName}`,
                    email: user.email ?? '',
                    groupName: user.groupName,
                    groupId: user.groupId,
                    tags: user.tags ?? [],
                    healthCategory: this.getHealthCategory(mskScore?.riskFactor?.riskCode),
                    toeTouchScore: mskScore?.movementScore?.toeTouchScore && (mskScore?.movementScore?.toeTouchScore.percentage || mskScore.movementScore.toeTouchScore.percentage === 0) ? mskScore?.movementScore?.toeTouchScore.percentage : undefined,
                    toeTouchPain: mskScore?.movementScore?.toeTouchScore?.score === 0 ? true : false,
                    shoulderMobilityScore: mskScore?.movementScore?.shoulderMobilityScore && (mskScore?.movementScore?.shoulderMobilityScore.percentage || mskScore.movementScore.shoulderMobilityScore.percentage === 0) ? mskScore?.movementScore?.shoulderMobilityScore.percentage : undefined,
                    shoulderMobilityPain: mskScore?.movementScore?.shoulderMobilityScore?.score === 0 ? true : false,
                    rotationScore: mskScore?.movementScore?.rotationScore && (mskScore?.movementScore?.rotationScore.percentage || mskScore.movementScore.rotationScore.percentage === 0) ? mskScore?.movementScore?.rotationScore.percentage : undefined,
                    rotationPain: mskScore?.movementScore?.rotationScore?.score === 0 ? true : false,
                    squatScore: mskScore?.movementScore?.squatScore && (mskScore?.movementScore?.squatScore.percentage || mskScore.movementScore.squatScore.percentage === 0) ? mskScore?.movementScore?.squatScore.percentage : undefined,
                    squatPain: mskScore?.movementScore?.squatScore?.score === 0 ? true : false,
                    balanceScore: mskScore?.movementScore?.balanceScore && (mskScore?.movementScore?.balanceScore.percentage || mskScore.movementScore.balanceScore.percentage === 0) ? mskScore?.movementScore?.balanceScore.percentage : undefined,
                    balancePain: mskScore?.movementScore?.balanceScore?.score === 0 ? true : false,
                    shoulderClearing: mskScore?.movementScore?.hasShoulderClearingPain ? mskScore?.movementScore?.hasShoulderClearingPain : false,
                    spineClearing: mskScore?.movementScore?.hasSpineClearingPain ? mskScore?.movementScore?.hasSpineClearingPain : false,
                    movementScore: mskScore?.movementScore?.percentage || mskScore?.movementScore?.percentage === 0 ? mskScore?.movementScore?.percentage : undefined,
                    movementFocus: movementFocus && movementFocus.shortLabel ? i18n.t(movementFocus.shortLabel) : i18n.t('general.none'),
                    movementFocusValue: movementFocus?.value ? movementFocus.value : undefined,
                }
            })
            .filter(u => u.mskScore != null);


        result.totalUsers = result.users.length;

        for (const user of result.users) {
            const focus = user.movementFocusValue;
            if (user.inPain) {
                result.usersInPainCount++;
            }

            if (
                focus === GroupIds.TOE_TOUCH ||
                focus === GroupIds.TOE_TOUCH_BREATHING
            ) {
                result.toeTouchCount++;
            }

            if (
                focus === GroupIds.SHOULDER_MOBILITY ||
                focus === GroupIds.SHOULDER_MOBILITY_BREATHING
            ) {
                result.shoulderMobilityCount++;
            }

            if (
                focus === GroupIds.ROTATION ||
                focus === GroupIds.ROTATION_BREATHING
            ) {
                result.rotationCount++;
            }

            if (
                focus === GroupIds.SQUAT ||
                focus === GroupIds.SQUAT_BREATHING
            ) {
                result.squatCount++;
            }

            if (
                focus === GroupIds.BALANCE ||
                focus === GroupIds.BALANCE_BREATHING
            ) {
                result.balanceCount++;
            }

            if (
                focus === GroupIds.SHOULDER_CLEARING ||
                focus === GroupIds.SHOULDER_CLEARING_BREATHING
            ) {
                result.shoulderClearingCount++;
            }

            if (
                focus === GroupIds.SPINE_CLEARING ||
                focus === GroupIds.SPINE_CLEARING_BREATHING
            ) {
                result.spineClearingCount++;
            }
        }


        return result;
    };

    public static grabInitialDynamicSummaryData = async (
        organizationId: string,
        selectedReportMonth: string
    ): Promise<ReportDynamicSummaryData> => {


        const result: ReportDynamicSummaryData = {
            focusAreas: [],
            history: [],
            organizationName: "",
            pendingUsers: 0,
            reportDate: selectedReportMonth ?? DateTime.now().startOf("month").toISODate(),
            riskFactorCodeCounts: [],
            scoreBreakdown: [],
            movementFocusAreas: [],
            totalUsers: 0,
            users: [],
            latestMskScoresByDate: [],
            completedLatestMskScores: [],
            isPastReport: false,
        };

        /* ---------------- ORGANIZATION ---------------- */
        const organization = await OrganizationService.get(organizationId);

        if (!organization?.name) return result;
        result.organizationName = organization.name;

        /* ---------------- USERS ---------------- */
        const users = (await this.getReportUsers(organizationId, undefined, undefined, this.getEndOfMonth(selectedReportMonth)))
        result.users = users;

        if (users.length === 0) return result;

        result.totalUsers = users?.length || 0;
        result.pendingUsers = users?.filter((u) => u.authenticationId == null).length || 0;

        let startDate = DateTime.fromISO(selectedReportMonth).minus({ months: 9 });
        const endDate = DateTime.fromISO(selectedReportMonth);
        const dates: DateTime[] = [];

        while (startDate.diff(endDate, "month").months <= -1) {
            dates.push(startDate.startOf("month"));
            startDate = startDate.plus({ month: 1 });
        }

        let isPastReport = false;

        if (endDate.diffNow("month").months <= -1) {
            // We are running a report for a month in the past
            dates.push(endDate.startOf("month"));
            isPastReport = true;
        }
        result.isPastReport = isPastReport;

        /* ---------------- SCORES (PARALLEL) ---------------- */ //TODO

        let latestMskScores: MskScore[] = [];

        // Build chunks once
        const userChunks: string[][] = [];

        for (let i = 0; i < Math.ceil(users.length / 10); i++) {
            const chunk = users
                .slice(i * 10, Math.min((i + 1) * 10, users.length))
                .map(u => u.id)
                .filter((id): id is string => !!id);

            userChunks.push(chunk);
        }

        const earliestDate = dates.length > 0
            ? dates[0].toISODate()
            : endDate.toISODate();
        const latestDate = endDate.toISODate();

        // ONE QUERY PER CHUNK (not per month)
        const chunkPromises = userChunks.map(async (chunk, index) => {

            const result = await mskScoreMonthlyMskScoreService.getBy([
                { field: "userId", operator: "in", value: chunk },
                { field: "date", operator: ">=", value: earliestDate },
                { field: "date", operator: "<=", value: latestDate }
            ]);


            return result;
        });

        const chunkResults = await Promise.all(chunkPromises);
        const allScores = chunkResults.flat();


        // Group scores by month
        const scoresByMonth = new Map<string, MskScore[]>();

        for (const score of allScores) {
            if (!score.date) continue;

            if (!scoresByMonth.has(score.date)) {
                scoresByMonth.set(score.date, []);
            }

            scoresByMonth.get(score.date)!.push(score);
        }

        for (const date of dates) {
            const dateKey = date.toISODate();
            const scores = scoresByMonth.get(dateKey) ?? [];

            result.latestMskScoresByDate.push({
                date: dateKey,
                scores
            });

            const percentages = scores
                .map(d => d.percentage)
                .filter(d => d != null);

            result.history.push({
                date: dateKey,
                percent:
                    percentages.length === 0
                        ? -1
                        : Math.round(
                            percentages.reduce((a, b) => a + b) / percentages.length
                        )
            });

            if (date.diff(endDate, "month").months === 0) {
                latestMskScores = scores;
            }
        }

        if (!isPastReport) {
            const currentUserChunks: string[][] = [];
            for (let i = 0; i < Math.ceil(users.length / 10); i++) {
                const chunk = users
                    .slice(i * 10, Math.min((i + 1) * 10, users.length))
                    .map(u => u.id)
                    .filter((id): id is string => !!id);

                currentUserChunks.push(chunk);
            }

            // Run all chunk queries in parallel
            const chunkPromises = currentUserChunks.map(async (chunk, index) => {

                const result = await UserLatestMskScoreService.getBy([
                    {
                        field: "userId",
                        operator: "in",
                        value: chunk,
                    }
                ]);


                return result;
            });

            const chunkResults = await Promise.all(chunkPromises);
            const allCurrentMonthScores = chunkResults.flat();

            // Only push ONCE (important improvement)
            result.latestMskScoresByDate.push({
                date: endDate.toISODate(),
                scores: allCurrentMonthScores
            });

            latestMskScores = latestMskScores.concat(allCurrentMonthScores);

            const percentages = latestMskScores
                .map(d => d.percentage)
                .filter(d => d != null);

            const percent =
                percentages.length === 0
                    ? -1
                    : Math.round(percentages.reduce((a, b) => a + b) / percentages.length);

            result.history.push({
                date: endDate.startOf("month").toISODate(),
                percent
            });
        }

        /* ---------------- FILTER + MAP ---------------- */

        // set summaries of completed MSKs
        // logic from old team here might be wrong, this filters completed msks with a percentage of 0. Not sure if this is intended.
        latestMskScores = latestMskScores.filter((score) => score.percentage);
        result.completedLatestMskScores = latestMskScores.filter((score) => score.percentage);

        if (latestMskScores.length > 0) {
            result.focusAreas = Utils.getFocusAreaCounts(latestMskScores);
            result.riskFactorCodeCounts = Utils.getRiskFactorCodeCounts(latestMskScores);
            result.scoreBreakdown = Utils.getMskScoreBreakdowns(latestMskScores);
            result.movementFocusAreas = Utils.getMovementFocusAreaCounts(latestMskScores);
            result.movementFocusAreas.push(Utils.getPainCorrectiveCount(latestMskScores))
        }

        result.history.reverse();

        return result;
    };

    public static grabInitialDynamicComplianceData = async (
        organizationId: string,
    ): Promise<ReportDynamicComplianceData> => {


        const result: ReportDynamicComplianceData = {
            users: [],
            usersLatestMskScore: [],
            userActivities: [],
            organizationName: "",
            pendingAssessmentUsers: [],
            pendingLoginUsers: [],
            pendingRetest: [],
            pendingUsers: 0,
            reportDate: DateTime.now().toISODate(),
            totalUsers: 0,
            usersAssessment: 0,
            usersCompletedTasks: 0,
            usersLoggedIn: 0,
            startDate: "",
            endDate: "",
            groupNames: [],
            tagNames: [],
        };

        /* ---------------- ORGANIZATION ---------------- */
        const organization = await OrganizationService.get(organizationId);

        if (!organization?.name) return result;
        result.organizationName = organization.name;

        /* ---------------- USERS ---------------- */
        const unfilteredUsers = await this.getReportUsers(organizationId)
        const users = this.filterUsersByStatus(unfilteredUsers);
        result.users = users;

        if (users.length === 0) return result;

        result.totalUsers = users.length;
        result.pendingLoginUsers = users
            .filter((u) => u.authenticationId == null)
            .map((u) => ({ id: u.id!, displayText: u.email! }));
        result.pendingUsers = result.pendingLoginUsers.length || 0;

        const startDate = organization.created?.seconds ? DateTime.fromSeconds(organization.created.seconds).toISODate() : DateTime.now().toISODate();
        const endDate = DateTime.now().toISODate();

        result.startDate = startDate;
        result.endDate = endDate;

        /* ---------------- SCORES (PARALLEL) ---------------- */


        const batchPromises: Promise<MskScore[]>[] = [];

        for (let i = 0; i < Math.ceil(users.length / 10); i++) {
            const batchUserIds = users
                .slice(i * 10, (i + 1) * 10)
                .map(u => u.id);


            const promise = (
                UserLatestMskScoreService.getBy([
                    { field: "userId", operator: "in", value: batchUserIds }
                ])
            ).finally(() => {
            });

            batchPromises.push(promise);
        }

        // Run all batches in parallel
        const batchResults = await Promise.all(batchPromises);

        // Flatten results
        const scores = batchResults.flat();
        result.usersLatestMskScore = scores;

        result.usersAssessment = scores.filter((score) => score.percentage != null).length;
        const mskScoreUserIds = scores.filter((score) => score.percentage != null).map((score) => score.userId);
        result.pendingAssessmentUsers = users
            .filter((u) => u.authenticationId != null)
            .filter((u) => mskScoreUserIds.indexOf(u.id!) === -1)
            .map((u) => ({ id: u.id!, displayText: `${u.firstName} ${u.lastName}` }));

        result.pendingRetest = users
            .filter((u) => u.authenticationId != null)
            .filter((u) => mskScoreUserIds.indexOf(u.id!) !== -1)
            .filter((u) => !u.wellnessProgramId)
            .map((u) => ({ id: u.id!, displayText: u.email ?? `${u.firstName} ${u.lastName}` }));


        const activityBatchPromises: Promise<UserActivity[]>[] = [];

        for (let i = 0; i < Math.ceil(users.length / 10); i++) {

            const batchUserIds = users
                .slice(i * 10, (i + 1) * 10)
                .map(u => u.id);


            const promise = UserActivityService.getBy([
                {
                    field: "date",
                    operator: ">=",
                    value: startDate,
                },
                {
                    field: "date",
                    operator: "<=",
                    value: endDate,
                },
                {
                    field: "userId",
                    operator: "in",
                    value: batchUserIds,
                }
            ]).finally(() => {
            });

            activityBatchPromises.push(promise);
        }

        // Run all batches in parallel
        const activityBatchResults = await Promise.all(activityBatchPromises);

        // Flatten results
        const userActivity = activityBatchResults.flat();

        result.userActivities = userActivity;



        const userIdsCompletedActivities = [...new Set(userActivity.filter((activity) => activity.type === ActivityType.Completion).map((activity) => activity.userId))];
        const totalDays = Math.ceil(DateTime.fromISO(endDate).diff(DateTime.fromISO(startDate), ["days"]).days / 2);
        result.usersCompletedTasks = userIdsCompletedActivities
            .filter((userId) => userActivity.filter((activity) => activity.userId === userId && activity.type === ActivityType.Completion).length >= totalDays).length;
        result.usersLoggedIn = [...new Set(userActivity.filter((activity) => activity.type === ActivityType.Open).map((activity) => activity.userId))].length;

        return result;
    };

    public static filterInitialDynamicComplianceData = (
        complianceData: ReportDynamicComplianceData,
        filters: FilterState,
        searchQuery: string
    ): ReportDynamicComplianceData => {


        const result: ReportDynamicComplianceData = {
            users: [],
            usersLatestMskScore: [],
            userActivities: [],
            organizationName: "",
            pendingAssessmentUsers: [],
            pendingLoginUsers: [],
            pendingRetest: [],
            pendingUsers: 0,
            reportDate: complianceData.reportDate,
            totalUsers: 0,
            usersAssessment: 0,
            usersCompletedTasks: 0,
            usersLoggedIn: 0,
            startDate: "",
            endDate: "",
            groupNames: [],
            tagNames: [],
        };

        /* ---------------- USERS ---------------- */
        result.users = complianceData.users.filter((user) => {
            if (
                searchQuery &&
                (!user.email?.toLowerCase().includes(searchQuery.toLowerCase()) &&
                    !`${user.firstName} ${user.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()))
            ) {
                return false;
            }
            // Group filter
            if (
                filters.groupIds.length > 0 &&
                (!user.groupId || !filters.groupIds.includes(user.groupId))
            ) {
                return false;
            }

            // Tag filter
            if (filters.tagIds.length > 0 &&
                !user.tags?.some((tag: any) => filters.tagIds.includes(tag.id))) {
                return false;
            }
            return true
        });

        if (result.users.length === 0) return result;

        result.totalUsers = result.users.length;
        result.pendingLoginUsers = result.users
            .filter((u) => u.authenticationId == null)
            .map((u) => ({ id: u.id!, displayText: u.email! }));
        result.pendingUsers = result.pendingLoginUsers.length || 0;

        result.startDate = complianceData.startDate;
        result.endDate = complianceData.endDate;

        /* ---------------- SCORES (PARALLEL) ---------------- */

        result.usersLatestMskScore = complianceData.usersLatestMskScore.filter((score) => {
            const user = result.users.find((u) => u.id === score.userId);
            return !!user;
        });

        result.usersAssessment = result.usersLatestMskScore.filter((score) => score.percentage != null).length;
        const mskScoreUserIds = result.usersLatestMskScore.filter((score) => score.percentage != null).map((score) => score.userId);
        result.pendingAssessmentUsers = result.users
            .filter((u) => u.authenticationId != null)
            .filter((u) => mskScoreUserIds.indexOf(u.id!) === -1)
            .map((u) => ({ id: u.id!, displayText: `${u.firstName} ${u.lastName}` }));

        result.pendingRetest = result.users
            .filter((u) => u.authenticationId != null)
            .filter((u) => mskScoreUserIds.indexOf(u.id!) !== -1)
            .filter((u) => !u.wellnessProgramId)
            .map((u) => ({ id: u.id!, displayText: u.email ?? `${u.firstName} ${u.lastName}` }));

        result.userActivities = complianceData.userActivities.filter((activity) => {
            const user = result.users.find((u) => u.id === activity.userId);
            return !!user;
        });

        const userIdsCompletedActivities = [...new Set(result.userActivities.filter((activity) => activity.type === ActivityType.Completion).map((activity) => activity.userId))];
        const totalDays = Math.ceil(DateTime.fromISO(result.endDate).diff(DateTime.fromISO(result.startDate), ["days"]).days / 2);
        result.usersCompletedTasks = userIdsCompletedActivities
            .filter((userId) => result.userActivities.filter((activity) => activity.userId === userId && activity.type === ActivityType.Completion).length >= totalDays).length;
        result.usersLoggedIn = [...new Set(result.userActivities.filter((activity) => activity.type === ActivityType.Open).map((activity) => activity.userId))].length;
        result.organizationName = complianceData.organizationName;

        return result;
    };

    public static grabInitialDynamicWellnessSummaryData = async (
        organizationId: string,
    ): Promise<ReportDynamicWellnessSummaryData> => {


        const result: ReportDynamicWellnessSummaryData = {
            averageEngagement: -1,
            sevenDayEngagement: -1,
            thirtyDayEngagement: -1,
            averageDaysMissed: -1,
            averageStreak: -1,
            activePrograms: -1,
            pendingRetest: -1,
            enrolledUsers: -1,
            userData: [],
            organizationName: "",
            reportDate: DateTime.now().toISODate(), // TODO translate
            totalUsers: -1,
            usersAndWellnessPrograms: []
        };

        /* ---------------- ORGANIZATION ---------------- */
        const organization = await OrganizationService.get(organizationId);

        if (!organization?.name) return result;
        result.organizationName = organization.name;

        /* ---------------- USERS ---------------- */
        const users = await this.getReportWellnessSummaryUsers(organizationId);
        result.usersAndWellnessPrograms = users;

        if (users.length === 0) return result;

        result.totalUsers = users.length;

        let totalEngagement = 0;
        let countIncluded = 0;

        let sevenDayTotal = 0;
        let sevenDayCount = 0;

        let thirtyDayTotal = 0;
        let thirtyDayCount = 0;

        let totalBestStreak = 0;
        let bestStreakCount = 0;

        let totalDaysMissed = 0;
        let daysMissedCount = 0;

        let activePrograms = 0;
        let enrolledUsers = 0;
        let pendingRetest = 0;

        for (const user of users) {
            const u = user.user;
            const wp = user.wellnessProgram;

            const progress = wp.currentProgress ?? [];
            const progressLength = progress.length;
            const lengthInDays = wp.lengthInDays ?? 0;

            let completedCount = 0;
            let daysMissed = 0;

            // Single-pass over progress (instead of 2 filters)
            for (const p of progress) {
                if (p === WellnessProgramActivity.Complete) completedCount++;
                else if (p === WellnessProgramActivity.Incomplete) daysMissed++;
            }

            // Average Engagement
            if (progressLength > 0) {
                const engagement = completedCount / progressLength;
                totalEngagement += engagement;
                countIncluded++;
            }

            totalDaysMissed += daysMissed;
            daysMissedCount++;

            // Program Status + Global Counters
            let programStatus: WellnessProgramStatus | undefined = undefined;

            if (u.wellnessProgramId && wp.startDateLocalDateTimeISO) {
                programStatus = WellnessProgramStatus.Active;
                activePrograms++;
            } else if (u.wellnessProgramId && !wp.startDateLocalDateTimeISO) {
                programStatus = WellnessProgramStatus.Pending;
                enrolledUsers++;
            } else if (!u.wellnessProgramId && wp) {
                programStatus = WellnessProgramStatus.Ended;
                pendingRetest++;
            }

            const daysLeft = Math.max(lengthInDays - progressLength, 0);

            // Streak
            if (u.bestDayStreak != null) {
                totalBestStreak += u.bestDayStreak;
                bestStreakCount++;
            } else if (u.dayStreak != null) {
                totalBestStreak += u.dayStreak;
                bestStreakCount++;
            }

            // 7-Day Engagement (no filter())
            let sevenDayPercent = 0;
            const last7 = u.lastSevenDays;

            if (Array.isArray(last7) && last7.length === 7) {
                let completed7 = 0;
                for (let i = 0; i < 7; i++) {
                    if (last7[i] === true) completed7++;
                }

                const sevenEngagement = completed7 / 7;
                sevenDayTotal += sevenEngagement;
                sevenDayCount++;
                sevenDayPercent = sevenEngagement * 100;
            }

            // 30-Day Engagement (no filter())
            let thirtyDayPercent = 0;
            const last30 = u.lastThirtyDays;

            if (Array.isArray(last30) && last30.length === 30) {
                let completed30 = 0;
                for (let i = 0; i < 30; i++) {
                    if (last30[i] === true) completed30++;
                }

                const thirtyEngagement = completed30 / 30;
                thirtyDayTotal += thirtyEngagement;
                thirtyDayCount++;
                thirtyDayPercent = thirtyEngagement * 100;
            }

            const progressPercent =
                lengthInDays > 0 ? (progressLength / lengthInDays) * 100 : 0;

            result.userData.push({
                id: u.id!,
                name: `${u.firstName} ${u.lastName}`,
                programStatus,
                daysLeft,
                daysMissed,
                currentStreak: u.dayStreak ?? 0,
                sevenDayEngagement: Math.round(sevenDayPercent),
                thirtyDayEngagement: Math.round(thirtyDayPercent),
                progressPercentage: Math.round(progressPercent),
            });
        }

        // Final Global Metrics (after the loop)
        result.averageEngagement =
            countIncluded > 0 ? Math.round((totalEngagement / countIncluded) * 100) : 0;

        result.sevenDayEngagement =
            sevenDayCount > 0 ? Math.round((sevenDayTotal / sevenDayCount) * 100) : 0;

        result.thirtyDayEngagement =
            thirtyDayCount > 0 ? Math.round((thirtyDayTotal / thirtyDayCount) * 100) : 0;

        result.averageStreak =
            bestStreakCount > 0 ? Math.round(totalBestStreak / bestStreakCount) : 0;

        result.averageDaysMissed =
            daysMissedCount > 0 ? Math.round(totalDaysMissed / daysMissedCount) : 0;

        result.activePrograms = activePrograms;
        result.enrolledUsers = enrolledUsers;
        result.pendingRetest = pendingRetest;


        return result;
    };

    public static filterInitialDynamicWellnessSummaryData = (
        wellnessSummaryData: ReportDynamicWellnessSummaryData,
        filters: FilterState,
        searchQuery: string
    ): ReportDynamicWellnessSummaryData => {


        const result: ReportDynamicWellnessSummaryData = {
            averageEngagement: -1,
            sevenDayEngagement: -1,
            thirtyDayEngagement: -1,
            averageDaysMissed: -1,
            averageStreak: -1,
            activePrograms: -1,
            pendingRetest: -1,
            enrolledUsers: -1,
            userData: [],
            usersAndWellnessPrograms: [],
            organizationName: "",
            reportDate: wellnessSummaryData.reportDate, // TODO translate
            totalUsers: -1,
        };

        /* ---------------- USERS ---------------- */
        result.usersAndWellnessPrograms = wellnessSummaryData.usersAndWellnessPrograms.filter((userAndProgram) => {
            const { user } = userAndProgram;
            if (
                searchQuery &&
                !`${user.firstName} ${user.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
            ) {
                return false;
            }
            // Group filter
            if (
                filters.groupIds.length > 0 &&
                (!user.groupId || !filters.groupIds.includes(user.groupId))
            ) {
                return false;
            }

            // Tag filter
            if (filters.tagIds.length > 0 &&
                !user.tags?.some((tag: any) => filters.tagIds.includes(tag.id))) {
                return false;
            }
            return true
        });

        if (result.usersAndWellnessPrograms.length === 0) return result;

        result.totalUsers = result.usersAndWellnessPrograms.length;

        let totalEngagement = 0;
        let countIncluded = 0;

        let sevenDayTotal = 0;
        let sevenDayCount = 0;

        let thirtyDayTotal = 0;
        let thirtyDayCount = 0;

        let totalBestStreak = 0;
        let bestStreakCount = 0;

        let totalDaysMissed = 0;
        let daysMissedCount = 0;

        let activePrograms = 0;
        let enrolledUsers = 0;
        let pendingRetest = 0;

        for (const userAndProgram of result.usersAndWellnessPrograms) {
            const { user, wellnessProgram } = userAndProgram;
            const progress = wellnessProgram.currentProgress ?? [];
            const progressLength = progress.length;
            const lengthInDays = wellnessProgram.lengthInDays ?? 0;

            // Average Engagement (Complete / Total in currentProgress)
            if (progressLength > 0) {
                const completedCount = progress.filter(
                    (p) => p === WellnessProgramActivity.Complete
                ).length;

                const engagement = completedCount / progressLength;
                totalEngagement += engagement;
                countIncluded++;
            }

            // Program Status + Global Counters
            let programStatus: WellnessProgramStatus | undefined = undefined; // Defaulting to this
            if (user.wellnessProgramId && wellnessProgram.startDateLocalDateTimeISO) {
                programStatus = WellnessProgramStatus.Active;
                activePrograms++;
            } else if (user.wellnessProgramId && !wellnessProgram.startDateLocalDateTimeISO) {
                programStatus = WellnessProgramStatus.Pending;
                enrolledUsers++;
            } else if (!user.wellnessProgramId && wellnessProgram) {
                programStatus = WellnessProgramStatus.Ended;
                pendingRetest++;
            }

            // Days Left
            const daysLeft = Math.max(lengthInDays - progressLength, 0);

            // Days Missed
            const daysMissed = progress.filter(
                (p) => p === WellnessProgramActivity.Incomplete
            ).length;

            totalDaysMissed += daysMissed;
            daysMissedCount++;

            // Streak (global average)
            if (user.bestDayStreak != null) {
                totalBestStreak += user.bestDayStreak;
                bestStreakCount++;
            } else if (user.dayStreak != null) {
                totalBestStreak += user.dayStreak;
                bestStreakCount++;
            }

            // 7-Day Engagement
            let sevenDayPercent = 0;
            const last7 = user.lastSevenDays;
            if (Array.isArray(last7) && last7.length === 7) {
                const completed7 = last7.filter((d) => d === true).length;
                const sevenEngagement = completed7 / 7;

                sevenDayTotal += sevenEngagement;
                sevenDayCount++;

                sevenDayPercent = sevenEngagement * 100;
            }

            // 30-Day Engagement
            let thirtyDayPercent = 0;
            const last30 = user.lastThirtyDays;
            if (Array.isArray(last30) && last30.length === 30) {
                const completed30 = last30.filter((d) => d === true).length;
                const thirtyEngagement = completed30 / 30;

                thirtyDayTotal += thirtyEngagement;
                thirtyDayCount++;

                thirtyDayPercent = thirtyEngagement * 100;
            }

            // Progress Percentage (Based on lengthInDays)
            const progressPercent =
                lengthInDays > 0 ? (progressLength / lengthInDays) * 100 : 0;

            // Push Per-User Result
            result.userData.push({
                id: user.id!,
                name: `${user.firstName} ${user.lastName}`,
                programStatus: programStatus,
                daysLeft,
                daysMissed,
                currentStreak: user.dayStreak ?? 0,
                sevenDayEngagement: Math.round(sevenDayPercent),
                thirtyDayEngagement: Math.round(thirtyDayPercent),
                progressPercentage: Math.round(progressPercent),
            });
        }

        // Final Global Metrics (after the loop)
        result.averageEngagement =
            countIncluded > 0 ? Math.round((totalEngagement / countIncluded) * 100) : 0;

        result.sevenDayEngagement =
            sevenDayCount > 0 ? Math.round((sevenDayTotal / sevenDayCount) * 100) : 0;

        result.thirtyDayEngagement =
            thirtyDayCount > 0 ? Math.round((thirtyDayTotal / thirtyDayCount) * 100) : 0;

        result.averageStreak =
            bestStreakCount > 0 ? Math.round(totalBestStreak / bestStreakCount) : 0;

        result.averageDaysMissed =
            daysMissedCount > 0 ? Math.round(totalDaysMissed / daysMissedCount) : 0;

        result.activePrograms = activePrograms;
        result.enrolledUsers = enrolledUsers;
        result.pendingRetest = pendingRetest;

        return result;
    };

    public static filterInitialDynamicSummaryData = (
        summaryData: ReportDynamicSummaryData,
        filters: FilterState,
        selectedReportMonth: string,
    ): ReportDynamicSummaryData => {


        const result: ReportDynamicSummaryData = {
            users: [],
            latestMskScoresByDate: [],
            completedLatestMskScores: [],
            focusAreas: [],
            history: [],
            organizationName: "",
            pendingUsers: 0,
            reportDate: selectedReportMonth ?? DateTime.now().startOf("month").toISODate(),
            riskFactorCodeCounts: [],
            scoreBreakdown: [],
            movementFocusAreas: [],
            totalUsers: 0,
            isPastReport: summaryData.isPastReport,
        };

        result.organizationName = summaryData.organizationName;

        /* ---------------- USERS ---------------- */
        result.users = summaryData.users.filter((user) => {
            // Group filter
            if (
                filters.groupIds.length > 0 &&
                (!user.groupId || !filters.groupIds.includes(user.groupId))
            ) {
                return false;
            }

            // Tag filter
            if (filters.tagIds.length > 0 &&
                !user.tags?.some((tag: any) => filters.tagIds.includes(tag.id))) {
                return false;
            }
            return true
        });

        if (result.users.length === 0) return result;

        result.totalUsers = result.users.length;
        result.pendingUsers = result.users.filter((u) => u.authenticationId == null).length || 0;

        const filteredUserIds = new Set(result.users.map(u => u.id));

        // filter latest msk scores to only include those of users in the filtered list
        result.latestMskScoresByDate =
            summaryData.latestMskScoresByDate.map(dateGroup => ({
                ...dateGroup,
                scores: dateGroup.scores.filter(score =>
                    filteredUserIds.has(score.userId)
                ),
            }));
        result.completedLatestMskScores = summaryData.completedLatestMskScores.filter(score => filteredUserIds.has(score.userId));

        for (const group of result.latestMskScoresByDate) {
            const percentages = group.scores.map((d) => d.percentage).filter((d) => d != null);
            result.history.push({
                date: group.date,
                percent: percentages.length === 0 ? -1 : Math.round(percentages.reduce((a, b) => a + b) / percentages.length),
            });
        }


        if (result.completedLatestMskScores.length > 0) {
            result.focusAreas = Utils.getFocusAreaCounts(result.completedLatestMskScores);
            result.riskFactorCodeCounts = Utils.getRiskFactorCodeCounts(result.completedLatestMskScores);
            result.scoreBreakdown = Utils.getMskScoreBreakdowns(result.completedLatestMskScores);
            result.movementFocusAreas = Utils.getMovementFocusAreaCounts(result.completedLatestMskScores);
            result.movementFocusAreas.push(Utils.getPainCorrectiveCount(result.completedLatestMskScores))
        }

        result.history.reverse();

        return result;
    };

    // #endregion Public Methods

    // -----------------------------------------------------------------------------------------
    // #region Private Methods
    // -----------------------------------------------------------------------------------------

    private static filterUsersByWellnessProgram = async (
        users: User[]
    ): Promise<{ user: User; wellnessProgram: WellnessProgram }[]> => {

        if (!users || users.length === 0) return [];

        const result = await Promise.all(
            users.map(async (u) => {
                let wp: WellnessProgram | null = null;

                if (u.wellnessProgramId) {
                    wp = await WellnessProgramService.get(u.wellnessProgramId);
                }

                // if they have no current wp, we want to find the latest one that was started.
                if (!wp) {
                    const conditions: FirestoreCondition[] = [
                        { field: "userId", operator: "==", value: u.id },
                        { field: "startDateLocalDateTimeISO", operator: "!=", value: null }
                    ];

                    const order: FirestoreOrder[] = [
                        { field: "created", direction: "desc" }
                    ];

                    const list = await WellnessProgramService.getBy(conditions, order, 1);
                    wp = list?.[0];
                }

                return { user: u, wellnessProgram: wp };
            })
        );

        return result.filter(
            (x): x is { user: User; wellnessProgram: WellnessProgram } =>
                x.wellnessProgram != null
        );
    };

    private static getReportWellnessSummaryUsers = async (
        organizationId: string,
        createdOnOrBefore?: string,
    ): Promise<{ user: User, wellnessProgram: WellnessProgram }[]> => {

        let users: User[] = [];

        users = await UserService.getBy(this.getReportUserConditions(organizationId));
        users = this.filterUsersByCreated(users, createdOnOrBefore);
        const wpUsers = await this.filterUsersByWellnessProgram(users);

        return wpUsers;
    }

    private static getReportUserConditions = (organizationId: string, groupIds?: string[]): FirestoreCondition[] => {
        const conditions: FirestoreCondition[] = [{
            field: "organizationId",
            operator: "==",
            value: organizationId,
        }];

        if (groupIds != null) {
            conditions.push({
                field: "groupId",
                operator: "in",
                value: groupIds,
            });
        }

        return conditions;
    }

    private static filterUsersByTags = (users: User[], tagIds?: string[]): User[] => {
        if (users == null) {
            return [];
        }
        if (users.length === 0 || tagIds == null || tagIds.length === 0) {
            return users;
        }

        const allowedUserIds: string[] = [];
        for (let i = 0; i < tagIds.length; i++) {
            const tagId = tagIds[i];
            users.filter((u) => u.tags != null && u.tags.find((userTag) => userTag.id === tagId) != null)
                .forEach((u) => {
                    allowedUserIds.push(u.id!)
                });
        }

        return users.filter((u) => allowedUserIds.indexOf(u.id!) >= 0);
    }

    private static filterUsersByCreated = (users: User[], createdOnOrBefore?: string): User[] => {
        if (users == null) {
            return [];
        }
        if (users.length === 0 || createdOnOrBefore == null || createdOnOrBefore.length === 0) {
            return users;
        }

        const unixCreate = Date.parse(createdOnOrBefore);
        if (isNaN(unixCreate)) {
            log.error(`filterUsersByCreated parsed date is NaN: "${createdOnOrBefore}"`, {
                createdOnOrBefore,
            });
            return [];
        }

        const seconds = Math.ceil(unixCreate / 1000) + 86400; // add 1 day to go through end of day

        return users
            .filter((x) => x.created != null)
            .filter((x) => (x.created as Timestamp).seconds < seconds);
    }

    private static getReportUsers = async (
        organizationId: string,
        groupIds?: string[],
        tagIds?: string[],
        createdOnOrBefore?: string,
    ): Promise<User[]> => {

        let users: User[] = [];

        if (groupIds != null && groupIds.length > 0) {
            for (let i = 0; i < Math.ceil(groupIds.length / 10); i++) {
                const curGroupIds = groupIds.slice(i * 10, Math.min((i + 1) * 10, groupIds.length));
                users = users.concat(
                    await UserService.getBy(this.getReportUserConditions(organizationId, curGroupIds))
                );
            }
        } else {
            users = await UserService.getBy(this.getReportUserConditions(organizationId));
        }
        users = this.filterUsersByCreated(users, createdOnOrBefore);
        users = this.filterUsersByTags(users, tagIds);

        return users;
    }

    private static getHealthCategory = (riskCode?: string): string => {
        if (riskCode == null) {
            return "";
        }

        if (riskCode.indexOf("-") === -1) {
            // handles "Performance"
            return riskCode;
        }

        return riskCode.substring(0, riskCode.indexOf("-")) ?? "";
    }

    private static getEndOfMonth = (date: string): string => {
        if (date == null || date.length === 0) {
            return date;
        }
        const reportDate = DateTime.fromISO(date);
        const endOfMonth = reportDate.endOf("month").toISODate();
        return endOfMonth;
    }

    private static isInPain = (mskScore: MskScore) => {
        if (mskScore.movementScore?.squatScore?.score === 0) {
            return true;
        }
        if (mskScore.movementScore?.rotationScore?.score === 0) {
            return true;
        }
        if (mskScore.movementScore?.shoulderMobilityScore?.score === 0) {
            return true;
        }
        if (mskScore.movementScore?.balanceScore?.score === 0) {
            return true;
        }
        if (mskScore.movementScore?.toeTouchScore?.score === 0) {
            return true;
        }
        return false;
    }

    private static buildFilterNames = (isInPain: boolean | null, healthCategories: string[], focusAreas: string[]): string[] => {
        const names: string[] = [];

        if (isInPain) {
            names.push(i18n.t('reports.movement.movementPain'));
        }
        else if (isInPain === false) {
            names.push(i18n.t('reports.movement.noMovementPain'));
        }

        // Map enum values to translated risk labels
        healthCategories.forEach(value => {
            const option = FullMskHealthRiskOptions.find(o => o.value === value);
            if (option?.label) {
                names.push(option.label);
            }
        });

        // Map focus area values to translated labels
        focusAreas.forEach(value => {
            const option = FocusGroupOptions.find(o => o.value === value);
            if (option?.label) {
                names.push(option.label);
            }
        });
        focusAreas.forEach(value => {
            const option = MovementFocusOptions.find(o => o.value === value);
            if (option?.shortLabel) {
                names.push(option.shortLabel);
            }
        });


        return names;
    };

    private static filterUsersByStatus = (users: User[]): User[] => {
        if (users == null) {
            return [];
        }
        if (users.length === 0) {
            return users;
        }

        return users.filter((u) => u.status !== UserStatus.Disabled);
    };
    // #endregion Private Methods


}
